// Copyright (C) Lembani Sakala - All rights reserved.
// Lembani Sakala.
// Unauthorized copying of this file, via any medium is strictly prohibited.
// Proprietary and confidential.

import { useEffect, useState } from 'react';
import axios from 'axios';
import { getToken } from '../../../utils/localStorage';

interface Enquiry {
  id: number;
  firstname: string;
  lastname: string;
  email: string;
  phone: string;
  message: string;
  status: string;
  createdAt: string;
}

interface PaginationData {
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export const EnquiryManagement = () => {
  const [enquiries, setEnquiries] = useState<Enquiry[]>([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState<number | null>(null);

  // Filters
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');

  // Pagination
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [pagination, setPagination] = useState<PaginationData>({
    total: 0,
    page: 1,
    limit: 10,
    totalPages: 0,
  });

  // Modal states
  const [selectedEnquiry, setSelectedEnquiry] = useState<Enquiry | null>(null);
  const [replySubject, setReplySubject] = useState('');
  const [replyMessage, setReplyMessage] = useState('');
  const [showReplyModal, setShowReplyModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [sendingReply, setSendingReply] = useState(false);

  // Toast notification
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);

  useEffect(() => {
    fetchEnquiries();
  }, [statusFilter, page, limit]);

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchTerm !== undefined) {
        fetchEnquiries();
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Date filter
  useEffect(() => {
    if (startDate || endDate) {
      fetchEnquiries();
    }
  }, [startDate, endDate]);

  const showToast = (message: string, type: 'success' | 'error') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  };

  const fetchEnquiries = async () => {
    try {
      setLoading(true);
      const token = getToken();
      const params = new URLSearchParams();

      if (statusFilter) params.append('status', statusFilter);
      if (searchTerm) params.append('search', searchTerm);
      if (startDate) params.append('startDate', startDate);
      if (endDate) params.append('endDate', endDate);
      params.append('page', page.toString());
      params.append('limit', limit.toString());

      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/admin/enquiries?${params.toString()}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      setEnquiries(response.data.data);
      setPagination(response.data.pagination);
    } catch (error) {
      console.error('Error fetching enquiries:', error);
      showToast('Failed to fetch enquiries', 'error');
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (id: number, status: string) => {
    try {
      setActionLoading(id);
      const token = getToken();
      await axios.patch(
        `${process.env.REACT_APP_API_URL}/admin/enquiries/${id}/status`,
        { status },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      showToast('Status updated successfully', 'success');
      fetchEnquiries();
    } catch (error) {
      console.error('Error updating status:', error);
      showToast('Failed to update status', 'error');
    } finally {
      setActionLoading(null);
    }
  };

  const handleReply = (enquiry: Enquiry) => {
    setSelectedEnquiry(enquiry);
    setReplySubject(`Re: Your enquiry - City Drive`);
    setReplyMessage('');
    setShowReplyModal(true);
  };

  const handleViewDetails = (enquiry: Enquiry) => {
    setSelectedEnquiry(enquiry);
    setShowDetailModal(true);
  };

  const sendReply = async () => {
    if (!selectedEnquiry || !replyMessage.trim()) return;

    try {
      setSendingReply(true);
      const token = getToken();
      await axios.post(
        `${process.env.REACT_APP_API_URL}/admin/enquiries/${selectedEnquiry.id}/reply`,
        {
          subject: replySubject,
          message: replyMessage,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      showToast('Reply sent successfully!', 'success');
      setShowReplyModal(false);
      fetchEnquiries();
    } catch (error) {
      console.error('Error sending reply:', error);
      showToast('Failed to send reply', 'error');
    } finally {
      setSendingReply(false);
    }
  };

  const deleteEnquiry = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this enquiry?')) return;

    try {
      setActionLoading(id);
      const token = getToken();
      await axios.delete(
        `${process.env.REACT_APP_API_URL}/admin/enquiries/${id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      showToast('Enquiry deleted successfully', 'success');
      fetchEnquiries();
    } catch (error) {
      console.error('Error deleting enquiry:', error);
      showToast('Failed to delete enquiry', 'error');
    } finally {
      setActionLoading(null);
    }
  };

  const clearFilters = () => {
    setStatusFilter('');
    setSearchTerm('');
    setStartDate('');
    setEndDate('');
    setPage(1);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Pending':
        return { bg: '#fef3c7', text: '#92400e', border: '#fbbf24' };
      case 'In Progress':
        return { bg: '#dbeafe', text: '#1e40af', border: '#3b82f6' };
      case 'Resolved':
        return { bg: '#d1fae5', text: '#065f46', border: '#10b981' };
      default:
        return { bg: '#e5e7eb', text: '#374151', border: '#9ca3af' };
    }
  };

  if (loading && enquiries.length === 0) {
    return (
      <div style={styles.loadingContainer}>
        <div style={styles.spinner} />
        <p style={styles.loadingText}>Loading enquiries...</p>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      {/* Toast Notification */}
      {toast && (
        <div style={{
          ...styles.toast,
          background: toast.type === 'success'
            ? 'linear-gradient(135deg, #48bb78 0%, #38a169 100%)'
            : 'linear-gradient(135deg, #f56565 0%, #e53e3e 100%)',
        }}>
          <span style={styles.toastIcon}>{toast.type === 'success' ? '✓' : '✕'}</span>
          {toast.message}
          <button style={styles.toastClose} onClick={() => setToast(null)}>×</button>
        </div>
      )}

      {/* Header Section */}
      <div style={styles.header}>
        <div>
          <h2 style={styles.pageTitle}>Enquiry Management</h2>
          <p style={styles.pageSubtitle}>
            {pagination.total} total enquiries • {pagination.totalPages} pages
          </p>
        </div>
        <div style={styles.headerActions}>
          <button style={styles.refreshButton} onClick={fetchEnquiries}>
            🔄
          </button>
          <button style={styles.clearButton} onClick={clearFilters}>
            Clear Filters
          </button>
        </div>
      </div>

      {/* Filters Section */}
      <div style={styles.filterCard}>
        <div style={styles.filterGrid}>
          <div style={styles.filterGroup}>
            <label style={styles.filterLabel}>🔍 Search</label>
            <input
              type="text"
              style={styles.searchInput}
              placeholder="Name, email, phone..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          <div style={styles.filterGroup}>
            <label style={styles.filterLabel}>📊 Status</label>
            <select
              style={styles.select}
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="">All Statuses</option>
              <option value="Pending">Pending</option>
              <option value="In Progress">In Progress</option>
              <option value="Resolved">Resolved</option>
            </select>
          </div>

          <div style={styles.filterGroup}>
            <label style={styles.filterLabel}>📅 Start Date</label>
            <input
              type="date"
              style={styles.dateInput}
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
            />
          </div>

          <div style={styles.filterGroup}>
            <label style={styles.filterLabel}>📅 End Date</label>
            <input
              type="date"
              style={styles.dateInput}
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
            />
          </div>
        </div>
      </div>

      {/* Enquiries Table/Cards */}
      <div style={styles.tableCard}>
        {loading ? (
          <div style={styles.centerContent}>
            <div style={styles.smallSpinner} />
          </div>
        ) : enquiries.length === 0 ? (
          <div style={styles.emptyState}>
            <div style={styles.emptyIcon}>📭</div>
            <h3 style={styles.emptyTitle}>No enquiries found</h3>
            <p style={styles.emptyText}>Try adjusting your filters or search terms</p>
          </div>
        ) : (
          <>
            {/* Desktop Table */}
            <div style={styles.tableWrapper}>
              <table style={styles.table}>
                <thead>
                  <tr style={styles.tableHeaderRow}>
                    <th style={styles.tableHeader}>ID</th>
                    <th style={styles.tableHeader}>Contact</th>
                    <th style={styles.tableHeader}>Message Preview</th>
                    <th style={styles.tableHeader}>Status</th>
                    <th style={styles.tableHeader}>Date</th>
                    <th style={{ ...styles.tableHeader, textAlign: 'center' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {enquiries.map((enquiry) => {
                    const statusColor = getStatusColor(enquiry.status);
                    return (
                      <tr key={enquiry.id} style={styles.tableRow}>
                        <td style={styles.tableCell}>
                          <span style={styles.idBadge}>#{enquiry.id}</span>
                        </td>
                        <td style={styles.tableCell}>
                          <div style={styles.contactCell}>
                            <div style={styles.avatar}>
                              {enquiry.firstname[0]}{enquiry.lastname[0]}
                            </div>
                            <div>
                              <div style={styles.contactName}>
                                {enquiry.firstname} {enquiry.lastname}
                              </div>
                              <div style={styles.contactEmail}>{enquiry.email}</div>
                              <div style={styles.contactPhone}>{enquiry.phone}</div>
                            </div>
                          </div>
                        </td>
                        <td style={styles.tableCell}>
                          <div style={styles.messagePreview}>
                            {enquiry.message.substring(0, 60)}
                            {enquiry.message.length > 60 && '...'}
                          </div>
                        </td>
                        <td style={styles.tableCell}>
                          <select
                            style={{
                              ...styles.statusBadge,
                              background: statusColor.bg,
                              color: statusColor.text,
                              borderColor: statusColor.border,
                            }}
                            value={enquiry.status}
                            onChange={(e) => updateStatus(enquiry.id, e.target.value)}
                            disabled={actionLoading === enquiry.id}
                          >
                            <option value="Pending">⏳ Pending</option>
                            <option value="In Progress">⚡ In Progress</option>
                            <option value="Resolved">✅ Resolved</option>
                          </select>
                        </td>
                        <td style={styles.tableCell}>
                          <div style={styles.dateText}>
                            {new Date(enquiry.createdAt).toLocaleDateString('en-US', {
                              month: 'short',
                              day: 'numeric',
                              year: 'numeric'
                            })}
                          </div>
                          <div style={styles.timeText}>
                            {new Date(enquiry.createdAt).toLocaleTimeString('en-US', {
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </div>
                        </td>
                        <td style={styles.tableCell}>
                          <div style={styles.actionButtons}>
                            <button
                              style={styles.viewButton}
                              onClick={() => handleViewDetails(enquiry)}
                              disabled={actionLoading === enquiry.id}
                              title="View Details"
                            >
                              👁️
                            </button>
                            <button
                              style={styles.replyButton}
                              onClick={() => handleReply(enquiry)}
                              disabled={actionLoading === enquiry.id}
                              title="Send Reply"
                            >
                              💬
                            </button>
                            <button
                              style={styles.deleteButton}
                              onClick={() => deleteEnquiry(enquiry.id)}
                              disabled={actionLoading === enquiry.id}
                              title="Delete"
                            >
                              {actionLoading === enquiry.id ? '⌛' : '🗑️'}
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </>
        )}

        {/* Pagination */}
        {pagination.totalPages > 1 && (
          <div style={styles.pagination}>
            <div style={styles.paginationInfo}>
              Showing {((page - 1) * limit) + 1} to {Math.min(page * limit, pagination.total)} of {pagination.total}
            </div>
            <div style={styles.paginationButtons}>
              <button
                style={{
                  ...styles.paginationButton,
                  ...(page === 1 ? styles.paginationButtonDisabled : {}),
                }}
                onClick={() => setPage(page - 1)}
                disabled={page === 1}
              >
                ← Previous
              </button>
              {[...Array(Math.min(pagination.totalPages, 5))].map((_, i) => {
                let pageNum = i + 1;
                if (pagination.totalPages > 5 && page > 3) {
                  pageNum = page - 2 + i;
                  if (pageNum > pagination.totalPages) pageNum = pagination.totalPages - (4 - i);
                }
                return (
                  <button
                    key={pageNum}
                    style={{
                      ...styles.paginationButton,
                      ...(page === pageNum ? styles.paginationButtonActive : {}),
                    }}
                    onClick={() => setPage(pageNum)}
                  >
                    {pageNum}
                  </button>
                );
              })}
              <button
                style={{
                  ...styles.paginationButton,
                  ...(page === pagination.totalPages ? styles.paginationButtonDisabled : {}),
                }}
                onClick={() => setPage(page + 1)}
                disabled={page === pagination.totalPages}
              >
                Next →
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Detail Modal */}
      {showDetailModal && selectedEnquiry && (
        <div style={styles.modalOverlay} onClick={() => setShowDetailModal(false)}>
          <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <h3 style={styles.modalTitle}>Enquiry Details</h3>
              <button style={styles.modalClose} onClick={() => setShowDetailModal(false)}>
                ×
              </button>
            </div>
            <div style={styles.modalBody}>
              <div style={styles.detailGrid}>
                <div style={styles.detailItem}>
                  <div style={styles.detailLabel}>👤 Name</div>
                  <div style={styles.detailValue}>
                    {selectedEnquiry.firstname} {selectedEnquiry.lastname}
                  </div>
                </div>
                <div style={styles.detailItem}>
                  <div style={styles.detailLabel}>📧 Email</div>
                  <div style={styles.detailValue}>{selectedEnquiry.email}</div>
                </div>
                <div style={styles.detailItem}>
                  <div style={styles.detailLabel}>📱 Phone</div>
                  <div style={styles.detailValue}>{selectedEnquiry.phone}</div>
                </div>
                <div style={styles.detailItem}>
                  <div style={styles.detailLabel}>📊 Status</div>
                  <div style={styles.detailValue}>
                    <span style={{
                      ...styles.statusBadge,
                      ...getStatusColor(selectedEnquiry.status),
                      display: 'inline-block',
                    }}>
                      {selectedEnquiry.status}
                    </span>
                  </div>
                </div>
              </div>
              <div style={styles.detailItem}>
                <div style={styles.detailLabel}>💬 Message</div>
                <div style={styles.messageBox}>{selectedEnquiry.message}</div>
              </div>
              <div style={styles.detailItem}>
                <div style={styles.detailLabel}>📅 Date Received</div>
                <div style={styles.detailValue}>
                  {new Date(selectedEnquiry.createdAt).toLocaleString()}
                </div>
              </div>
            </div>
            <div style={styles.modalFooter}>
              <button style={styles.secondaryButton} onClick={() => setShowDetailModal(false)}>
                Close
              </button>
              <button
                style={styles.primaryButton}
                onClick={() => {
                  setShowDetailModal(false);
                  handleReply(selectedEnquiry);
                }}
              >
                💬 Send Reply
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reply Modal */}
      {showReplyModal && selectedEnquiry && (
        <div style={styles.modalOverlay} onClick={() => !sendingReply && setShowReplyModal(false)}>
          <div style={{ ...styles.modal, maxWidth: '600px' }} onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <h3 style={styles.modalTitle}>
                Reply to {selectedEnquiry.firstname} {selectedEnquiry.lastname}
              </h3>
              <button
                style={styles.modalClose}
                onClick={() => setShowReplyModal(false)}
                disabled={sendingReply}
              >
                ×
              </button>
            </div>
            <div style={styles.modalBody}>
              <div style={styles.detailItem}>
                <div style={styles.detailLabel}>Original Message</div>
                <div style={styles.messageBox}>{selectedEnquiry.message}</div>
              </div>

              <div style={styles.detailItem}>
                <div style={styles.detailLabel}>Subject</div>
                <input
                  type="text"
                  style={styles.input}
                  value={replySubject}
                  onChange={(e) => setReplySubject(e.target.value)}
                  disabled={sendingReply}
                />
              </div>

              <div style={styles.detailItem}>
                <div style={styles.detailLabel}>Your Reply</div>
                <textarea
                  style={styles.textarea}
                  rows={6}
                  value={replyMessage}
                  onChange={(e) => setReplyMessage(e.target.value)}
                  placeholder="Type your reply here..."
                  disabled={sendingReply}
                />
              </div>
            </div>
            <div style={styles.modalFooter}>
              <button
                style={styles.secondaryButton}
                onClick={() => setShowReplyModal(false)}
                disabled={sendingReply}
              >
                Cancel
              </button>
              <button
                style={{
                  ...styles.primaryButton,
                  ...((!replyMessage.trim() || sendingReply) ? styles.buttonDisabled : {}),
                }}
                onClick={sendReply}
                disabled={!replyMessage.trim() || sendingReply}
              >
                {sendingReply ? '⌛ Sending...' : '📤 Send Reply'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  container: {
    position: 'relative',
  },
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '400px',
    gap: '16px',
  },
  spinner: {
    width: '48px',
    height: '48px',
    border: '4px solid #e9ecef',
    borderTop: '4px solid #667eea',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
  },
  smallSpinner: {
    width: '32px',
    height: '32px',
    border: '3px solid #e9ecef',
    borderTop: '3px solid #667eea',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
  },
  loadingText: {
    color: '#6c757d',
    fontSize: '16px',
    fontWeight: '500',
  },
  toast: {
    position: 'fixed',
    top: '24px',
    right: '24px',
    padding: '16px 24px',
    borderRadius: '12px',
    color: '#fff',
    fontSize: '14px',
    fontWeight: '600',
    boxShadow: '0 8px 24px rgba(0,0,0,0.2)',
    zIndex: 10000,
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    minWidth: '300px',
    animation: 'slideInRight 0.3s ease-out',
  },
  toastIcon: {
    fontSize: '20px',
  },
  toastClose: {
    marginLeft: 'auto',
    background: 'transparent',
    border: 'none',
    color: '#fff',
    fontSize: '24px',
    cursor: 'pointer',
    padding: 0,
    lineHeight: 1,
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '24px',
    flexWrap: 'wrap',
    gap: '16px',
  },
  pageTitle: {
    fontSize: '28px',
    fontWeight: '700',
    color: '#1a202c',
    margin: 0,
    marginBottom: '4px',
  },
  pageSubtitle: {
    fontSize: '14px',
    color: '#6c757d',
    margin: 0,
  },
  headerActions: {
    display: 'flex',
    gap: '12px',
    alignItems: 'center',
  },
  refreshButton: {
    padding: '10px',
    background: '#fff',
    border: '2px solid #e9ecef',
    borderRadius: '10px',
    fontSize: '18px',
    cursor: 'pointer',
    transition: 'all 0.2s',
    width: '40px',
    height: '40px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  clearButton: {
    padding: '10px 20px',
    background: '#fff',
    border: '2px solid #e9ecef',
    borderRadius: '10px',
    fontSize: '14px',
    fontWeight: '600',
    color: '#495057',
    cursor: 'pointer',
    transition: 'all 0.2s',
  },
  filterCard: {
    background: '#fff',
    borderRadius: '16px',
    padding: '24px',
    marginBottom: '24px',
    boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
  },
  filterGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: '20px',
  },
  filterGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  filterLabel: {
    fontSize: '13px',
    fontWeight: '600',
    color: '#495057',
  },
  searchInput: {
    padding: '12px 16px',
    border: '2px solid #e9ecef',
    borderRadius: '10px',
    fontSize: '14px',
    transition: 'all 0.2s',
    outline: 'none',
  },
  select: {
    padding: '12px 16px',
    border: '2px solid #e9ecef',
    borderRadius: '10px',
    fontSize: '14px',
    background: '#fff',
    cursor: 'pointer',
    transition: 'all 0.2s',
    outline: 'none',
  },
  dateInput: {
    padding: '12px 16px',
    border: '2px solid #e9ecef',
    borderRadius: '10px',
    fontSize: '14px',
    transition: 'all 0.2s',
    outline: 'none',
  },
  tableCard: {
    background: '#fff',
    borderRadius: '16px',
    boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    maxHeight: 'calc(100vh - 420px)',
  },
  centerContent: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: '48px',
  },
  emptyState: {
    textAlign: 'center',
    padding: '64px 24px',
  },
  emptyIcon: {
    fontSize: '64px',
    marginBottom: '16px',
  },
  emptyTitle: {
    fontSize: '20px',
    fontWeight: '700',
    color: '#1a202c',
    margin: 0,
    marginBottom: '8px',
  },
  emptyText: {
    fontSize: '14px',
    color: '#6c757d',
    margin: 0,
  },
  tableWrapper: {
    overflowX: 'auto',
    overflowY: 'auto',
    flex: 1,
    minHeight: 0,
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
  },
  tableHeaderRow: {
    background: '#f8f9fa',
    borderBottom: '2px solid #e9ecef',
  },
  tableHeader: {
    padding: '16px',
    textAlign: 'left',
    fontSize: '13px',
    fontWeight: '700',
    color: '#495057',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  tableRow: {
    borderBottom: '1px solid #e9ecef',
    transition: 'all 0.2s',
  },
  tableCell: {
    padding: '16px',
    fontSize: '14px',
    color: '#495057',
  },
  idBadge: {
    display: 'inline-block',
    padding: '4px 12px',
    background: '#e9ecef',
    borderRadius: '8px',
    fontSize: '13px',
    fontWeight: '700',
    color: '#495057',
  },
  contactCell: {
    display: 'flex',
    gap: '12px',
    alignItems: 'center',
  },
  avatar: {
    width: '40px',
    height: '40px',
    borderRadius: '10px',
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#fff',
    fontSize: '14px',
    fontWeight: '700',
    flexShrink: 0,
  },
  contactName: {
    fontSize: '14px',
    fontWeight: '600',
    color: '#1a202c',
    marginBottom: '2px',
  },
  contactEmail: {
    fontSize: '12px',
    color: '#6c757d',
    marginBottom: '2px',
  },
  contactPhone: {
    fontSize: '12px',
    color: '#6c757d',
  },
  messagePreview: {
    fontSize: '13px',
    color: '#6c757d',
    lineHeight: '1.5',
    maxWidth: '300px',
  },
  statusBadge: {
    padding: '6px 12px',
    borderRadius: '8px',
    fontSize: '13px',
    fontWeight: '600',
    border: '2px solid',
    cursor: 'pointer',
    transition: 'all 0.2s',
  },
  dateText: {
    fontSize: '13px',
    fontWeight: '600',
    color: '#495057',
    marginBottom: '2px',
  },
  timeText: {
    fontSize: '12px',
    color: '#6c757d',
  },
  actionButtons: {
    display: 'flex',
    gap: '8px',
    justifyContent: 'center',
  },
  viewButton: {
    padding: '8px 12px',
    background: '#e9ecef',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    transition: 'all 0.2s',
    fontSize: '16px',
  },
  replyButton: {
    padding: '8px 12px',
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    transition: 'all 0.2s',
    fontSize: '16px',
  },
  deleteButton: {
    padding: '8px 12px',
    background: '#fee',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    transition: 'all 0.2s',
    fontSize: '16px',
  },
  pagination: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '20px 24px',
    borderTop: '1px solid #e9ecef',
    flexWrap: 'wrap',
    gap: '16px',
  },
  paginationInfo: {
    fontSize: '14px',
    color: '#6c757d',
  },
  paginationButtons: {
    display: 'flex',
    gap: '8px',
  },
  paginationButton: {
    padding: '8px 16px',
    background: '#fff',
    border: '2px solid #e9ecef',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: '600',
    color: '#495057',
    cursor: 'pointer',
    transition: 'all 0.2s',
  },
  paginationButtonActive: {
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    color: '#fff',
    borderColor: '#667eea',
  },
  paginationButtonDisabled: {
    opacity: 0.5,
    cursor: 'not-allowed',
  },
  modalOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(0,0,0,0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 9999,
    padding: '24px',
  },
  modal: {
    background: '#fff',
    borderRadius: '16px',
    maxWidth: '500px',
    width: '100%',
    maxHeight: '90vh',
    overflow: 'auto',
    boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
  },
  modalHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '24px',
    borderBottom: '1px solid #e9ecef',
  },
  modalTitle: {
    fontSize: '20px',
    fontWeight: '700',
    color: '#1a202c',
    margin: 0,
  },
  modalClose: {
    background: 'transparent',
    border: 'none',
    fontSize: '32px',
    color: '#6c757d',
    cursor: 'pointer',
    padding: 0,
    lineHeight: 1,
  },
  modalBody: {
    padding: '24px',
    display: 'flex',
    flexDirection: 'column',
    gap: '20px',
  },
  detailGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: '16px',
  },
  detailItem: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  detailLabel: {
    fontSize: '13px',
    fontWeight: '700',
    color: '#495057',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  detailValue: {
    fontSize: '14px',
    color: '#1a202c',
  },
  messageBox: {
    padding: '16px',
    background: '#f8f9fa',
    borderRadius: '12px',
    fontSize: '14px',
    color: '#495057',
    lineHeight: '1.6',
    border: '1px solid #e9ecef',
  },
  input: {
    padding: '12px 16px',
    border: '2px solid #e9ecef',
    borderRadius: '10px',
    fontSize: '14px',
    outline: 'none',
    transition: 'all 0.2s',
  },
  textarea: {
    padding: '12px 16px',
    border: '2px solid #e9ecef',
    borderRadius: '10px',
    fontSize: '14px',
    outline: 'none',
    resize: 'vertical',
    fontFamily: 'inherit',
    transition: 'all 0.2s',
  },
  modalFooter: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '12px',
    padding: '24px',
    borderTop: '1px solid #e9ecef',
  },
  secondaryButton: {
    padding: '12px 24px',
    background: '#fff',
    border: '2px solid #e9ecef',
    borderRadius: '10px',
    fontSize: '14px',
    fontWeight: '600',
    color: '#495057',
    cursor: 'pointer',
    transition: 'all 0.2s',
  },
  primaryButton: {
    padding: '12px 24px',
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    border: 'none',
    borderRadius: '10px',
    fontSize: '14px',
    fontWeight: '600',
    color: '#fff',
    cursor: 'pointer',
    transition: 'all 0.2s',
  },
  buttonDisabled: {
    opacity: 0.5,
    cursor: 'not-allowed',
  },
};
